import { Box, ContentBlock, Divider, Stack } from 'braid-design-system';
import { useMemo } from 'react';

import { Background } from 'src/components/Background/Background';
import { Banner } from 'src/components/Banner';
import { HeroWithImage } from 'src/components/HeroWithImage/HeroWithImage';
import { IconFeatureBlock } from 'src/components/IconFeatureBlock/IconFeatureBlock';
import { ButtonLink } from 'src/components/Link/Link';
import { Nav } from 'src/components/Nav/Nav';
import { NavAlertBanner } from 'src/components/NavAlertBanner/NavAlertBanner';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import { PartnerIconListing } from 'src/components/PartnerIconListing/PartnerIconListing';
import HeroImg from 'src/images/hero-desk-lady.jpg';
import Icon1 from 'src/images/icon-1.png';
import Icon2 from 'src/images/icon-2.png';
import Icon3 from 'src/images/icon-3.png';
import { links } from 'src/links';
import type { PageProps } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { chooseConnectedPartners } from 'src/utils/partners';
import { slugify } from 'src/utils/slugify';

import { groupPartners } from '../ConnectedPartners/components/SearchFilter/makePartnerSuggestions';

export const ConnectWithSeek = ({ slug }: PageProps) => {
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  const connectedPartners = chooseConnectedPartners(
    useIntegrationPartners().partners,
  );

  const partnerData = useMemo(
    () =>
      connectedPartners.state === 'data'
        ? ({
            state: 'data',
            data: groupPartners(connectedPartners.data),
          } as const)
        : connectedPartners,
    [connectedPartners],
  );

  return (
    <PageWrapper pageTrackingProps={pageTrackingProps} title={slug}>
      <Nav activeTab="connect-with-seek" />
      <NavAlertBanner />
      <Stack space="xxlarge">
        <HeroWithImage
          heading="Seamless access to SEEK through our Connected Partners"
          text="Enjoy a more efficient hiring process by accessing SEEK’s job ads, tools and talent pool through SEEK’s Connected Partners."
          image={HeroImg}
        />
        <Banner
          heading="Link SEEK to your recruitment software provider"
          subHeading="Provide us with some basic information and we’ll set you up with access to SEEK products and tools via your connected partner."
          actions={
            <>
              <ButtonLink
                tone="brandAccent"
                href={links.LINK_YOUR_SOFTWARE.path}
                tracking={links.LINK_YOUR_SOFTWARE.tracking}
              >
                I’m an employer
              </ButtonLink>

              <ButtonLink
                tone="neutral"
                href={links.INTEGRATION_REQUEST.path}
                tracking={links.INTEGRATION_REQUEST.tracking}
              >
                I’m an ATS
              </ButtonLink>
            </>
          }
        />
        {partnerData.state === 'data' && (
          <>
            <PartnerIconListing
              heading="SEEK Premier Partners"
              text="Premier Partners support direct access and the best experience with SEEK’s product solutions to help you get the most out of your recruitment software."
              columns={[2, 3, 4]}
              data={partnerData.data['SEEK Premier Partners']}
            />
            <ContentBlock width="large">
              <Divider />
            </ContentBlock>
            <PartnerIconListing
              heading="SEEK Select Partners"
              text="Select Partners support access to SEEK’s product solutions to help you get the most out of your recruitment software."
              columns={[3, 4, 5]}
              limit={30}
              size="small"
              data={partnerData.data['SEEK Select Partners']}
              cta={{
                text: 'View our full list',
                link: links.CONNECTED_PARTERS.path,
                tracking: links.CONNECTED_PARTERS.tracking,
              }}
            />
          </>
        )}
        <Background color="#f1f7ff">
          <Box
            paddingY={['xlarge', 'xxlarge']}
            paddingX={['gutter', 'gutter', 'none']}
          >
            <IconFeatureBlock
              heading="How to access SEEK"
              text="Find out more about our products and Partners whose systems support our solutions."
              columns={[1, 3]}
              data={[
                {
                  text: 'Learn more about our product solutions.',
                  image: {
                    url: Icon1,
                    format: 'png',
                    width: '48px',
                  },
                  cta: {
                    text: links.WHATS_POSSIBLE.name,
                    href: links.WHATS_POSSIBLE.path,
                    tracking: links.WHATS_POSSIBLE.tracking,
                  },
                },
                {
                  text: 'Explore the Partners who can help you get the most value out of SEEK.',
                  image: {
                    url: Icon2,
                    format: 'png',
                    width: '48px',
                  },
                  cta: {
                    text: links.CONNECTED_PARTERS.name,
                    href: links.CONNECTED_PARTERS.path,
                    tracking: links.CONNECTED_PARTERS.tracking,
                  },
                },
                {
                  text: 'Review the latest implementation release notes by Partner.',
                  image: {
                    url: Icon3,
                    format: 'png',
                    width: '48px',
                  },
                  cta: {
                    text: links.RELEASE_NOTES.name,
                    href: links.RELEASE_NOTES.path,
                    tracking: links.RELEASE_NOTES.tracking,
                  },
                },
              ]}
            />
          </Box>
        </Background>
      </Stack>
    </PageWrapper>
  );
};
